import { useEffect, useMemo, useState } from 'react'
import { Grid, Typography } from '@mui/material'
import { KeywordItem } from '../../../modules/mpsSeo/reducer'
import { useStyles } from './KeywordDashboard.styles'
import { KeyWordRowV2 } from '../KeyWordRowV2/KeyWordRowV2'
import {
  getCompetitorLegendIds,
  getRankTooltip,
} from '../KeyWordRowV2/KeyWordRowV2.utils'
import { isNonEmptyNumber, ShortenedNameV2 } from '@labrav/common'
import {
  LabraToolTipV2,
  PercentageUpDown,
  VerticalDivider,
} from '@labrav/react-components'
import clsx from 'clsx'
import { useTheme } from '@mui/material/styles'

import { productDetailDate } from '../../../common/utils/productCreationUtil'
import { useTimeZone } from '../../../common/utils/hooks/useTimeZone'

interface KeywordDashboardProps {
  keywords: KeywordItem[]
}

const KeywordDashboard: React.FC<KeywordDashboardProps> = ({ keywords }) => {
  const [selectedIndex, setSelectedIndex] = useState<number>(0)
  const [legendSelectionsByKeyword, setLegendSelectionsByKeyword] = useState<
    Record<string, string[]>
  >({})

  useEffect(() => {
    setLegendSelectionsByKeyword(prev => {
      const next = { ...prev }
      let changed = false

      keywords.forEach(keyword => {
        const keywordName = keyword.keywordName
        const allLegendIds = getCompetitorLegendIds(keyword)

        if (!allLegendIds.length || prev[keywordName] !== undefined) {
          return
        }

        next[keywordName] = allLegendIds
        changed = true
      })

      return changed ? next : prev
    })
  }, [keywords])

  const activeKeyword = keywords[selectedIndex]
  const activeKeywordName = activeKeyword?.keywordName ?? ''
  const activeSelectedLegends = useMemo(
    () =>
      legendSelectionsByKeyword[activeKeywordName] ??
      getCompetitorLegendIds(activeKeyword),
    [activeKeyword, activeKeywordName, legendSelectionsByKeyword]
  )

  const theme = useTheme()
  const graphColors = [
    theme.palette.graphLines.red,
    theme.palette.graphLines.lavendar,
    theme.palette.graphLines.green,
    theme.palette.graphLines.orange,
    theme.palette.graphLines.brown,
    theme.palette.graphLines.pink,
    theme.palette.graphLines.yellow,
    theme.palette.graphLines.cyan,
    theme.palette.graphLines.darkGray,
    theme.palette.graphLines.teal,
    theme.palette.graphLines.lightBlue,
    theme.palette.graphLines.magenta,
    theme.palette.graphLines.limeGreen,
    theme.palette.graphLines.darkGold,
    theme.palette.activeField.main,
  ]

  const { timeZone } = useTimeZone()
  const { classes } = useStyles()
  return (
    <div>
      <Grid container direction="row">
        <Grid
          className={classes.sidebar}
          size={{
            md: 3,
          }}
        >
          <div className={classes.fullWidth}>
            <div className={classes.listWrapper}>
              <Typography className={classes.keywordRank}>Keyword</Typography>
              <Typography
                className={clsx(classes.keywordRank, classes.widthRank)}
              >
                Rank
              </Typography>
            </div>
            <div className={classes.horizontalDivider}></div>
            <div className={classes.scroll}>
              {keywords.map((keyword, index) => {
                const { tooltipMsg, upDown } = getRankTooltip(
                  keyword?.selfRankChange,
                  keyword?.selfInitialRank,
                  keyword?.selfLatestRank
                )

                return (
                  <LabraToolTipV2
                    key={keyword.keywordName}
                    label={
                      <div className={classes.tooltipContent}>
                        <div className={classes.tooltipWrapper}>
                          <div
                            className={classes.tooltipHeaderRow}
                            data-testid="keyword-tooltip-header"
                          >
                            <div
                              className={classes.tooltipHeaderCell}
                              data-testid="tooltip-column-competitors"
                            >
                              Competitors
                            </div>
                            <div
                              className={clsx(
                                classes.tooltipHeaderCell,
                                classes.tooltipHeaderCellNumeric
                              )}
                              data-testid="tooltip-column-current-rank"
                            >
                              Current Rank
                            </div>
                            <div
                              className={clsx(
                                classes.tooltipHeaderCell,
                                classes.tooltipHeaderCellNumeric,
                                classes.tooltipLastColumn
                              )}
                              data-testid="tooltip-column-rank-change"
                            >
                              Rank Change
                            </div>
                          </div>
                          <div
                            className={classes.tooltipHeaderDivider}
                            data-testid="tooltip-header-divider"
                          />
                          {keyword.competitors.map((item, competitorIndex) => {
                            const { upDown: competitorsRankUpDown } =
                              getRankTooltip(
                                item.rankChange,
                                item.initialRank,
                                item.currentRank
                              )

                            return (
                              <div
                                key={
                                  item.Id || `${item.name}-${competitorIndex}`
                                }
                                className={classes.tooltipRow}
                              >
                                <div className={classes.nameLegend}>
                                  <div className={classes.dot}>
                                    <span
                                      data-testid="legend-box"
                                      style={{
                                        backgroundColor:
                                          graphColors[competitorIndex] ||
                                          theme.palette.grey[400],
                                      }}
                                      className={classes.legendDot}
                                    />
                                  </div>
                                  <div className={classes.tooltipText}>
                                    <ShortenedNameV2
                                      name={item?.name || '-'}
                                      length={18}
                                    ></ShortenedNameV2>
                                  </div>
                                </div>
                                <div
                                  className={clsx(
                                    classes.tooltipText,
                                    classes.tooltipNumericCell
                                  )}
                                >
                                  {isNonEmptyNumber(item?.currentRank) &&
                                  item.currentRank !== 0
                                    ? item.currentRank
                                    : '-'}
                                </div>

                                {isNonEmptyNumber(item?.rankChange) &&
                                item.rankChange !== 0 &&
                                item.currentRank !== 0 ? (
                                  <div
                                    className={clsx(
                                      classes.tooltipNumericCell,
                                      classes.tooltipLastColumn
                                    )}
                                  >
                                    <PercentageUpDown
                                      percentage={Math.abs(item.rankChange)}
                                      isPercentage={false}
                                      type={competitorsRankUpDown}
                                      showBackgroundColor={false}
                                      fontSize="12px"
                                    />
                                  </div>
                                ) : (
                                  <div
                                    className={clsx(
                                      classes.tooltipText,
                                      classes.tooltipNumericCell,
                                      classes.tooltipLastColumn
                                    )}
                                  >
                                    -
                                  </div>
                                )}
                              </div>
                            )
                          })}
                        </div>
                        <div
                          className={classes.tooltipFooter}
                          data-testid="tooltip-last-refreshed"
                        >
                          {keyword.updatedAt
                            ? `Last refreshed on ${
                                productDetailDate(keyword.updatedAt, timeZone)
                                  .modifiedDate
                              }`
                            : 'Last refreshed on -'}
                        </div>
                      </div>
                    }
                    tooltipPlacement="right-end"
                  >
                    <div
                      className={`${classes.keywordItem} ${
                        selectedIndex === index ? classes.selectedKeyword : ''
                      }`}
                      onClick={() => setSelectedIndex(index)}
                    >
                      <div className={classes.keywordName}>
                        {keyword.keywordName}
                      </div>
                      <div
                        className={clsx(
                          classes.rankWrapper,
                          classes.keywordName,
                          classes.wrapperWidth
                        )}
                      >
                        {isNonEmptyNumber(keyword?.selfLatestRank) ? (
                          <div
                            className={clsx(
                              classes.keyword,
                              !isNonEmptyNumber(keyword?.selfRankChange) &&
                                keyword?.selfLatestRank &&
                                !!keyword?.selfRankChange
                                ? classes.rankingBorder
                                : ''
                            )}
                          >
                            {keyword?.selfLatestRank}
                          </div>
                        ) : (
                          <div className={classes.notAvilable}>-</div>
                        )}
                        {isNonEmptyNumber(keyword?.selfRankChange) &&
                          keyword?.selfLatestRank &&
                          !!keyword?.selfRankChange && (
                            <div className={classes.selfRankChange}>
                              <PercentageUpDown
                                percentage={Math.abs(keyword?.selfRankChange)}
                                isPercentage={false}
                                type={upDown}
                                showBackgroundColor={true}
                                fontSize="12px !important"
                              />
                            </div>
                          )}
                      </div>
                    </div>
                  </LabraToolTipV2>
                )
              })}
            </div>
          </div>
          <VerticalDivider height="auto" />
        </Grid>

        <Grid
          className={classes.content}
          size={{
            md: 9,
          }}
        >
          {keywords.length > 0 && activeKeyword && (
            <KeyWordRowV2
              keywordItem={activeKeyword}
              selectedLegends={activeSelectedLegends}
              onSelectedLegendsChange={nextSelection =>
                setLegendSelectionsByKeyword(prev => ({
                  ...prev,
                  [activeKeywordName]: nextSelection,
                }))
              }
            />
          )}
        </Grid>
      </Grid>
    </div>
  )
}

export default KeywordDashboard
